import {
  Alert,
  Attributes,
  type AttributesRow,
  type ButtonProps,
  getFilePreviewUrl,
  type Hyperlink,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNil, isNotNil, reject} from 'ramda';

import {type GetFileByIdApiResponse} from '@dms/api/base';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {
  composePath,
  type Nullish,
  openFile,
  type RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {type PkwEnvkvState} from '../utils/derivePkwEnvkvState';

export interface PkwEnvkvCardBodyProps extends RequiredTestIdProps {
  vehicleId: string;
  state: PkwEnvkvState | Nullish;
  calculationRows: AttributesRow[];
  document: GetFileByIdApiResponse | undefined;
  // End link of the in-progress status alert (the retry of a running attempt).
  statusHyperlink?: Hyperlink;
}

export function PkwEnvkvCardBody(props: PkwEnvkvCardBodyProps) {
  const state = props.state;
  if (isNil(state)) {
    return null;
  }

  const openDocumentPreview = () => {
    if (isNotNil(props.document)) {
      openFile(getFilePreviewUrl(props.document.originalUri, props.document.contentType));
    }
  };

  const documentHyperlink: Hyperlink | undefined = isNotNil(state.previewFileId)
    ? {
        leftIcon: 'action/open_in_new',
        title: i18n.t('general.actions.view'),
        href: isNotNil(props.document)
          ? getFilePreviewUrl(props.document.originalUri, props.document.contentType)
          : undefined,
        isDisabled: isNil(props.document),
        onClick: (event) => {
          event.preventDefault();
          openDocumentPreview();
        },
      }
    : undefined;
  // One edit link, to the tab of the first correction.
  const editTab = state.readinessAlert?.vehicleEditTabs.at(0);
  const editVehicleAction: ButtonProps | undefined = isNotNil(editTab)
    ? {
        variant: 'link',
        size: 'small',
        leftIcon: 'image/edit',
        title: i18n.t('entity.vehicle.actions.goToEditVehicle'),
        to: composePath(vehiclesRoutes.editV2Tab, {params: {id: props.vehicleId, tab: editTab}}),
      }
    : undefined;
  // The same document link as an alert action, for an alert that lists issues.
  const documentAction: ButtonProps | undefined = isNotNil(documentHyperlink)
    ? {
        variant: 'link',
        size: 'small',
        leftIcon: documentHyperlink.leftIcon,
        title: documentHyperlink.title,
        to: documentHyperlink.href ?? undefined,
        isDisabled: documentHyperlink.isDisabled,
        onClick: documentHyperlink.onClick,
      }
    : undefined;
  const readinessIssues = state.readinessAlert?.issues ?? [];

  return (
    <VStack spacing={2}>
      <Show when={isNotNil(state.readinessAlert) && readinessIssues.length === 0}>
        <Alert
          type="inline"
          variant={state.readinessAlert?.variant}
          title={state.readinessAlert?.title}
          hyperlinks={isNotNil(documentHyperlink) ? [documentHyperlink] : undefined}
          data-testid={suffixTestId('readiness', props)}
        />
      </Show>
      <Show when={isNotNil(state.readinessAlert) && readinessIssues.length > 0}>
        <Alert
          type="inline"
          layout="detailed"
          variant={state.readinessAlert?.variant}
          title={state.readinessAlert?.title}
          message={readinessIssues}
          actions={reject(isNil, [documentAction, editVehicleAction])}
          data-testid={suffixTestId('readiness', props)}
        />
      </Show>
      <Show when={isNotNil(state.eligibilityAlert)}>
        <Alert
          type="inline"
          variant={state.eligibilityAlert?.variant}
          title={state.eligibilityAlert?.title}
          hyperlinks={isNotNil(documentHyperlink) ? [documentHyperlink] : undefined}
        />
      </Show>
      <Show when={isNotNil(state.statusAlert) && isNil(state.statusAlert?.message)}>
        <Alert
          type="inline"
          variant={state.statusAlert?.variant}
          title={state.statusAlert?.title}
          hyperlinks={isNotNil(props.statusHyperlink) ? [props.statusHyperlink] : undefined}
        />
      </Show>
      <Show when={isNotNil(state.statusAlert?.message)}>
        <Alert
          type="inline"
          layout="detailed"
          variant={state.statusAlert?.variant}
          title={state.statusAlert?.title}
          message={state.statusAlert?.message}
        />
      </Show>
      <Show when={state.areCalculationRowsVisible}>
        <Attributes
          rows={props.calculationRows}
          size="half"
          data-testid={suffixTestId('calculation', props)}
        />
      </Show>
    </VStack>
  );
}
